import {
  Conversation,
  ConversationContext,
  ConversationStatus,
} from '@stripe-internal/conversations-client';
import {Api} from '../../Providers/ApiProvider/api/api';
import {
  isAssistantTriggerPoint,
  isDotComCsfTriggerPoint,
  isRestartableTriggerPoint,
} from '../../helpers/triggerPoints';

export const inputEnabled = (conversation: Conversation) => {
  const isEnabled =
    !conversation.context.enabledInputModes ||
    (conversation.context.enabledInputModes as string[]).includes('text') ||
    isConversationRestartable(conversation);

  return isEnabled;
};

export const isChannelChat = (conversation: Conversation) =>
  conversation.context.channel === 'chat';

export const isChannelRAC = (conversation: Conversation) =>
  conversation.context.channel === 'request_a_call';

export const isRACInProgress = (conversation: Conversation) =>
  isChannelRAC(conversation) &&
  !(conversation.status === ConversationStatus.Inactive);
export const isLlmConversation = (conversation: Conversation) =>
  conversation.context.channel === 'bot';

export const attachmentsEnabled = (conversation: Conversation) => {
  return (
    isChannelChat(conversation) ||
    (conversation.context.enabledInputModes &&
      (conversation.context.enabledInputModes as string[]).includes(
        'attachments',
      ))
  );
};

// Disable interactive message elements when:
// - the conversation has ended
// - user has transitioned to live chat with an agent
// - user has requested a call
export const isMessageInteractivityEnabled = (conversation: Conversation) =>
  !conversation.hasEnded &&
  !isChannelChat(conversation) &&
  !isChannelRAC(conversation);

export const requestsEmail = (conversation: Conversation) => {
  return (
    conversation.context.intent === 'contact_support' &&
    conversation.context.requestedChannel === 'email'
  );
};

export const requestsRAC = (conversation: Conversation) => {
  return (
    conversation.context.channel === 'request_a_call' ||
    (conversation.context.intent === 'contact_support' &&
      conversation.context.requestedChannel === 'request_a_call')
  );
};

export function isConversationFromAssistant(
  conversation: Conversation,
): boolean {
  return isAssistantTriggerPoint(
    conversation.context.triggerParams?.triggerPoint,
  );
}

const DOT_COM_ENTRY_POINTS = ['dot_com', 'dot_com_embedded_csf'];

export function isDotComEntryPoint(entryPoint?: unknown): boolean {
  return DOT_COM_ENTRY_POINTS.includes(entryPoint as string);
}

export function isDotComAudience(
  audience?: unknown,
  entryPoint?: unknown,
): boolean {
  return audience === 'dot_com' && isDotComEntryPoint(entryPoint);
}

export function isDotComConversation(conversation: Conversation): boolean {
  return isDotComEntryPoint(conversation.context.entryPoint);
}

export function isConversationFromCsf(
  conversation?: Conversation,
  context?: ConversationContext,
): boolean {
  const conversationContext = conversation?.context || context;
  return isDotComCsfTriggerPoint(
    conversationContext?.triggerParams?.triggerPoint,
  );
}

export function isConversationRestartable(conversation: Conversation) {
  return (
    conversation.status === ConversationStatus.Inactive &&
    isRestartableTriggerPoint(conversation.context.triggerParams?.triggerPoint)
  );
}

export const userHasInteractedWithConversation = (
  conversation: Conversation,
): boolean => {
  const userMessages = Object.values(conversation.messages).filter(
    (message) => message.participant.role === 'user',
  );

  const interactiveMessages = userMessages.filter((message) => {
    // For options message, the user has only interacted if they selected an option
    if (
      message.type === 'select-options-conversation-message' ||
      message.type === 'button-options-conversation-message'
    ) {
      return !!message.selectedOptionId;
    }

    return true;
  });

  return interactiveMessages.length > 0;
};

export const isConversationEligibleForFeedback = async ({
  api,
  conversation,
}: {
  api: Api;
  conversation: Conversation;
}): Promise<boolean> => {
  if (isConversationFromAssistant(conversation)) {
    return false;
  }

  const isSelfServe = conversation.context.channel === 'bot';
  const userHasInteracted = userHasInteractedWithConversation(conversation);

  if (!isSelfServe || !userHasInteracted) {
    return false;
  }

  try {
    const feedbackData = await api.fetchFeedback({
      conversationId: conversation.id,
    });

    return (
      feedbackData.didWeResolve !== 'resolved' &&
      feedbackData.eligibleForFeedback
    );
  } catch {
    return false;
  }
};

export const isConversationEligibleForCSAT = (
  conversation: Conversation,
): boolean => {
  if (isConversationFromAssistant(conversation)) {
    return false;
  }

  const isSelfServe = conversation.context.channel === 'bot';
  const userHasInteracted = userHasInteractedWithConversation(conversation);

  return isSelfServe && userHasInteracted;
};

export function isConversationEligibleForDotComCSAT(
  conversation: Conversation,
): boolean {
  return (
    isDotComConversation(conversation) &&
    userHasInteractedWithConversation(conversation)
  );
}

export function shouldConversationSendPageContext(
  conversation: Conversation,
): boolean {
  return (
    !!conversation &&
    (isConversationFromAssistant(conversation) ||
      isDotComConversation(conversation))
  );
}
